import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { IntegrationService } from '../../../services/Integration/integration.service';
import { IntegrationPermissionService } from '../../../services/Integration/integration-permission.service';
import { IntegrationErrorNotificationService } from '../../../services/Integration/integration-error-notification.service';
import {
  SyncResult,
  ConnectionTestResponse,
  SyncResults,
  SyncProvider,
} from '../../../models/integration.model';
import { SyncResultsModalComponent } from '../sync-results-modal/sync-results-modal.component';
import { TypedSyncResult } from '../../../models/integration.model';
import { QueueStatusService } from 'app/services/Integration/queue-status.service';
import { UserService } from 'app/core/user/user.service';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from '../confirm-dialog/confirm-dialog.component';

/**
 * Generic Sync Button Component
 * Provides a button to trigger synchronization for different providers (projects, p6, observations)
 * Following existing component patterns in the codebase
 */
@Component({
  selector: 'app-project-sync-button',
  templateUrl: './project-sync-button.component.html',
  styleUrls: ['./project-sync-button.component.scss'],
})
export class ProjectSyncButtonComponent implements OnInit, OnDestroy {
  // Input properties for provider configuration
  @Input() provider: SyncProvider = 'projects';
  @Input() projectId?: string;
  @Input() testMode: boolean = false;
  @Input() showConnectionTest: boolean = true;
  @Input() showLastResults: boolean = true;
  // Whether the project this button targets is actually linked to Unifier. Only meaningful for
  // per-project providers ('p6', 'observations'); defaults true so the global 'projects' sync,
  // which has no single project to check, is unaffected. A project without a project number
  // always fails NWC's validation, so the button is disabled rather than left to fail (PC-2377).
  @Input() unifierLinked: boolean = true;
  // Whether this project is an NWC Cluster or Region rather than a real project (PC-2381). A
  // container is never itself linked (see unifierLinked), but unlike a genuinely broken project
  // its button stays enabled: clicking it syncs every real, linked project underneath it instead
  // of refusing outright. Defaults false so unrelated providers/projects are unaffected.
  @Input() isHierarchyContainer: boolean = false;

  // Public properties
  isLoading: boolean = false;
  canSync: boolean = false;
  lastSyncTime: Date | null = null;
  enableProjectUnifier: boolean = false;

  // Provider-specific labels
  providerLabels = {
    projects: {
      syncButton: 'integration.sync_projects',
      connectionTestButton: 'integration.test_connection',
      lastResultsButton: 'integration.show_last_results',
      syncQueued: 'integration.sync_queued',
      lastSyncKey: 'lastProjectSync',
    },
    p6: {
      syncButton: 'integration.sync_p6_metrics',
      connectionTestButton: 'integration.test_connection',
      lastResultsButton: 'integration.show_last_results',
      syncQueued: 'integration.p6_sync_queued',
      lastSyncKey: 'lastP6Sync',
    },
    observations: {
      syncButton: 'integration.sync_observations',
      connectionTestButton: 'integration.test_connection',
      lastResultsButton: 'integration.show_last_results',
      syncQueued: 'integration.observations_sync_queued',
      lastSyncKey: 'lastObservationsSync',
    },
  };

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _integrationService: IntegrationService,
    private _permissionService: IntegrationPermissionService,
    private _errorService: IntegrationErrorNotificationService,
    private _queueStatusService: QueueStatusService,
    private _dialog: MatDialog,
    private _transloco: TranslocoService,
    private userService: UserService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  async ngOnInit() {
    this.canSync = await this._permissionService.canSyncProvider(this.provider);

    this._queueStatusService.queueCompleted$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ provider, projectId }) => {
        const isSameProvider = provider === this.provider;
        const isSameProject =
          provider === 'projects' || this.projectId === (projectId || undefined);

        if (isSameProvider && isSameProject) {
          this._syncLastSyncTimeFromTracker();
        }
      });

    this._loadLastSyncTime();
    this._syncLastSyncTimeFromTracker();

    // Check for last completed queue from backend first
    this._queueStatusService.checkLastCompletedQueue(this.provider, this.projectId);

    // Check for saved processing queue before starting normal polling
    this._queueStatusService.checkSavedProcessingQueue(this.provider, this.projectId);

    this._queueStatusService.startPollingImmediately(this.provider, this.projectId);
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    this._queueStatusService.stopPolling();
    this._queueStatusService.stopTracking();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get provider-specific label
   */
  getLabel(key: string): string {
    const labels = this.providerLabels[this.provider];
    return this._transloco.translate(labels[key as keyof typeof labels] || key);
  }

  /**
   * Cluster/Region sync wording. The button keeps the label it has on a single project, and the
   * scope is said in the tooltip and the confirmation. Observations are shown to NWC users as
   * "transactions", so that is the unit the text names; the P6 button on the same container has
   * its own wording, since telling a user it syncs "transactions" there would be wrong (PC-2381).
   */
  getHierarchyText(part: 'tooltip' | 'confirm_title' | 'confirm_message'): string {
    const suffix = this.provider === 'p6' ? '_p6' : '';
    return this._transloco.translate(`integration.sync_hierarchy_${part}${suffix}`);
  }

  /**
   * Format date based on current language
   * Uses Arabic locale for Arabic language and English locale for English
   */
  formatDateByLocale(date: Date): string {
    const currentLang = this._transloco.getActiveLang();
    const locale = currentLang === 'ar' ? 'ar-EG' : 'en-GB';

    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }

  /**
   * Handle sync button click
   */
  onSync(testData: any = null): void {
    if (!this.canSync || this.isLoading) {
      return;
    }

    if (!this.unifierLinked && !this.isHierarchyContainer) {
      this._errorService.showWarning(
        this._transloco.translate('integration.project_not_linked_to_unifier'),
      );
      return;
    }

    if (!this.unifierLinked && this.isHierarchyContainer) {
      // A Cluster/Region: this click can fan out to a large number of projects (seen up to
      // ~150 on prod), so confirm before firing rather than queuing silently on a single click.
      const dialogRef = this._dialog.open(ConfirmDialogComponent, {
        maxWidth: '480px',
        minWidth: '300px',
        data: new ConfirmDialogModel(
          this.getHierarchyText('confirm_title'),
          this.getHierarchyText('confirm_message'),
          undefined,
          undefined,
          undefined,
          this._transloco.translate('integration.sync_hierarchy_continue'),
        ),
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this._runSync(testData);
        }
      });
      return;
    }

    this._runSync(testData);
  }

  private _runSync(testData: any = null): void {
    this.isLoading = true;

    const testMode = localStorage.getItem('syncTestMode');
    if (testMode) {
      this.testMode = testMode === 'true';
    } else {
      this.testMode = false;
    }

    this._integrationService
      .sync(this.provider, {
        testMode: this.testMode,
        projectId: this.projectId,
        customData: testData,
      })
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (result: SyncResult) => {
          this.isLoading = false;
          this._queueStatusService.startPollingImmediately(this.provider, this.projectId);

          // Someone else (or an earlier click) already has a sync running for this scope: nothing
          // new was queued, so say that and show the run's progress instead (PC-2422).
          if (result.data?.already_running) {
            this._errorService.showInfo(
              this._transloco.translate('integration.sync_already_in_progress'),
            );
            return;
          }

          this.lastSyncTime = new Date();
          this._saveLastSyncTime();
          if (result.status === 'success') {
            this._errorService.showSuccess(this.getLabel('syncQueued'));
          }
        },
        error: (error) => {
          this.isLoading = false;
          this._errorService.handleSyncError(error);
        },
      });
  }

  /**
   * Test connection to Unifier
   */
  onTestConnection(): void {
    if (!this.canSync || this.isLoading) {
      return;
    }

    this.isLoading = true;

    this._integrationService
      .testUnifierConnection()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (result: ConnectionTestResponse) => {
          this.isLoading = false;

          // Check if the API call was successful
          if (result.status === 'success') {
            // Check the actual connection test result from the data object
            if (result.data && result.data.status === 'success') {
              this._errorService.showSuccess(
                this._transloco.translate('integration.connection_test_successful'),
              );
            } else {
              this._errorService.showError(
                this._transloco.translate('integration.connection_test_failed'),
              );
            }
          } else {
            this._errorService.showError(
              this._transloco.translate('integration.connection_test_failed'),
            );
          }
        },
        error: (error) => {
          this.isLoading = false;
          this._errorService.handleSyncError(error);
        },
      });
  }

  /**
   * Show sync results in modal
   */
  private _showSyncResults(result: SyncResult | SyncResults): void {
    const dialogRef = this._dialog.open(SyncResultsModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: { syncResult: result },
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((refreshData) => {
      if (refreshData) {
        // Trigger data refresh if needed
      }
    });
  }

  /**
   * Load last sync time from localStorage
   */
  private _loadLastSyncTime(): void {
    const labels = this.providerLabels[this.provider];
    const lastSyncKey = this._getProjectSpecificSyncKey(labels.lastSyncKey);
    const lastSyncTimeStr = localStorage.getItem(lastSyncKey);
    if (lastSyncTimeStr) {
      this.lastSyncTime = new Date(lastSyncTimeStr);
    }
  }

  /**
   * Save last sync time to localStorage
   */
  private _saveLastSyncTime(): void {
    const labels = this.providerLabels[this.provider];
    const lastSyncKey = this._getProjectSpecificSyncKey(labels.lastSyncKey);
    if (this.lastSyncTime) {
      localStorage.setItem(lastSyncKey, this.lastSyncTime.toISOString());
    }
  }

  /**
   * Align lastSyncTime with the latest queue tracker data
   * so backend-triggered completions show in the UI.
   */
  private _syncLastSyncTimeFromTracker(): void {
    const tracker = this._queueStatusService.getQueueTracker(this.provider, this.projectId);
    const trackerTimestamp = tracker?.timestamp ? new Date(tracker.timestamp) : null;

    if (!trackerTimestamp) {
      return;
    }

    const current = this.lastSyncTime?.getTime() || 0;
    if (trackerTimestamp.getTime() > current) {
      this.lastSyncTime = trackerTimestamp;
      this._saveLastSyncTime();
    }
  }

  /**
   * Check if last sync results have not been viewed
   */
  private _lastSyncResultsNotViewed(): boolean {
    const tracker = this._queueStatusService.getQueueTracker(this.provider, this.projectId);
    return tracker && tracker.completed && !tracker.viewed;
  }

  /**
   * Get project-specific sync key
   * For P6 and observations: include project ID in the key
   * For projects: use global key (projects sync is global)
   */
  private _getProjectSpecificSyncKey(baseKey: string): string {
    // Projects sync is global, P6 and observations are project-specific
    if ((this.provider === 'p6' || this.provider === 'observations') && this.projectId) {
      return `${baseKey}_${this.projectId}`;
    }
    return baseKey;
  }

  /**
   * Handle last sync results button click
   */
  onLastSyncResultsClicked(result: TypedSyncResult): void {
    // Show appropriate results modal based on provider type
  }

  /**
   * Handle sync projects button click (legacy method)
   */
  onSyncProjects(): void {
    this.onSync();
  }
}
